import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../i18n';
import { isAdmin, setState, useStore } from '../store/store';
import { readUrlState } from '../lib/urlState';
import { Icon } from '../panels/Icon';
import { getData, loadGameData, setData, useData } from './dataStore';
import { DataSidebar } from './DataSidebar';
import { RecordView } from './RecordView';
import { CategoryTable, ChangesTable } from './CategoryTable';
import { CategorySheet } from './CategorySheet';
import { SHEETS } from './sheet';
import { CHANGES, isPending } from './DataSidebar';
import { categoryIcon } from './categories';

/**
 * The "Speldata" view: categories and records on the left; in the centre the selected record, or the
 * whole category as a table (the balancing view) or a sheet (kalkylark), or the changes waiting for review.
 */
export function DataWorkspace() {
  const loaded = useData((s) => s.loaded);
  const record = useData((s) => (s.selectedId ? s.records[s.selectedId] : undefined));
  const category = useData((s) => s.category);
  const view = useData((s) => (s.category ? s.views[s.category] : undefined));
  const hasCategories = useData((s) => s.categories.length > 0);
  const records = useData((s) => s.records);
  const pending = useMemo(() => Object.values(records).filter(isPending), [records]);
  useDataDeepLink();

  useEffect(() => {
    void loadGameData();
  }, []);

  return (
    <>
      <DataSidebar />
      <main className="center data-center">
        {record ? (
          <RecordView key={record.id} record={record} />
        ) : loaded && hasCategories && category === CHANGES ? (
          <ChangesTable records={pending} />
        ) : loaded && hasCategories && category && view === 'sheet' && SHEETS[category] ? (
          <CategorySheet key={category} category={category} />
        ) : loaded && hasCategories && category ? (
          <CategoryTable key={category} category={category} />
        ) : (
          <DataHome loaded={loaded} />
        )}
      </main>
    </>
  );
}

function DataHome({ loaded }: { loaded: boolean }) {
  const { t } = useTranslation();
  const user = useStore((s) => s.user);
  const categories = useData((s) => s.categories);
  const records = useData((s) => s.records);
  const lastImport = useData((s) => s.lastImport);
  const loading = useData((s) => s.loading);
  const counts = useMemo(() => {
    const out: Record<string, number> = {};
    for (const r of Object.values(records)) out[r.category] = (out[r.category] ?? 0) + 1;
    return out;
  }, [records]);

  if (!loaded || loading) return <div className="splash">{t('common.loading')}</div>;
  if (categories.length === 0)
    return (
      <div className="splash">
        <div className="data-empty">
          <p>{t('data.empty')}</p>
          {isAdmin(user) && (
            <button className="primary" onClick={() => setState({ adminOpen: 'gamedata' })}>
              {t('data.importNow')}
            </button>
          )}
        </div>
      </div>
    );
  return (
    <div className="record data-home">
      <h2>{t('data.title')}</h2>
      <p className="muted">{t('data.intro')}</p>
      {lastImport && (
        <p className="small muted">
          {t('data.lastImport', { date: formatDate(lastImport.created) })}
          {lastImport.unityGitCommit && (
            <span className="mono"> · {lastImport.unityGitCommit.slice(0, 8)}</span>
          )}
        </p>
      )}
      <div className="cat-grid">
        {categories.map((c) => (
          <button
            key={c.key}
            className="card cat-card"
            onClick={() => setData({ category: c.key, search: '' })}
          >
            <Icon id={categoryIcon(c.key)} size={20} />
            <span>{t(`data.categories.${c.key}`, { defaultValue: c.label })}</span>
            <span className="muted small">{counts[c.key] ?? 0}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/** /data/<category>?r=<guid | record id> — reads it once on open, then keeps the address bar in step. */
function useDataDeepLink() {
  const loaded = useData((s) => s.loaded);
  const category = useData((s) => s.category);
  const selected = useData((s) => (s.selectedId ? s.records[s.selectedId] : undefined));
  const pending = useRef<ReturnType<typeof readUrlState> | null>(readUrlState());

  useEffect(() => {
    const want = pending.current;
    if (!loaded || !want) return;
    pending.current = null;
    const records = Object.values(getData().records);
    const r = want.record ? records.find((x) => x.guid === want.record || x.id === want.record) : undefined;
    if (r) setData({ selectedId: r.id, category: r.category });
    else if (
      want.dataCategory &&
      (want.dataCategory === '__changes' || getData().categories.some((c) => c.key === want.dataCategory))
    )
      setData({ category: want.dataCategory });
  }, [loaded]);

  useEffect(() => {
    if (!loaded || pending.current) return;
    const cat = category === '__changes' ? category : (selected?.category ?? category);
    const url = `/data${cat ? `/${cat}` : ''}${selected ? `?r=${selected.guid || selected.id}` : ''}`;
    if (location.pathname + location.search !== url) history.replaceState(null, '', url);
  }, [loaded, category, selected]);
}
